import { z } from "zod";
import { zEmail, zId, zIsoDate, zMoney } from "./common.js";
import { zGiftChannel, zPaymentMethod } from "./giving.js";
import type { Permission } from "./permissions.js";

/**
 * CT-RM-21: integration contracts.
 *
 * (1) Zapier-ready API — REST hooks (subscribe/unsubscribe), polling/sample
 *     lists per trigger, an auth-test ("me") endpoint, and idempotent actions.
 *     Authentication is our OAuth 2.0 server (RM-19) or an API token; every
 *     endpoint is guarded by permission scopes (scopes ∩ the user's role).
 * (2) QuickBooks Online live sync — connection, per-gift sync state, preview.
 */

// ---------------------------------------------------------------------------
// Zapier triggers

/**
 * Each trigger is backed by a webhook event (REST hooks reuse the webhook
 * outbox) and needs a read scope to subscribe to or poll.
 */
export const ZAPIER_TRIGGERS = {
  new_constituent: { event: "constituent.created", permission: "constituent:read", label: "New Constituent" },
  updated_constituent: { event: "constituent.updated", permission: "constituent:read", label: "Updated Constituent" },
  new_gift: { event: "gift.created", permission: "gift:read", label: "New Gift" },
  tag_added: { event: "constituent.tag_added", permission: "constituent:read", label: "Tag Added to Constituent" },
} as const satisfies Record<string, { event: string; permission: Permission; label: string }>;
export type ZapierTriggerKey = keyof typeof ZAPIER_TRIGGERS;
export const ZAPIER_TRIGGER_KEYS = Object.keys(ZAPIER_TRIGGERS) as [ZapierTriggerKey, ...ZapierTriggerKey[]];
export const zZapierTriggerKey = z.enum(ZAPIER_TRIGGER_KEYS);

/** POST /hooks — Zapier's performSubscribe body */
export const zHookSubscribe = z.object({
  targetUrl: z.string().trim().url().max(500),
  event: zZapierTriggerKey,
});
export type HookSubscribe = z.infer<typeof zHookSubscribe>;

export interface HookSubscription {
  id: string;
  event: ZapierTriggerKey;
  targetUrl: string;
  createdAt: string;
}

/** GET /zapier/triggers/:key — newest first; `id` is the dedupe key Zapier uses */
export const zTriggerPollQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type TriggerPollQuery = z.infer<typeof zTriggerPollQuery>;

/** Flat constituent shape for Zapier (polling items and REST hook payloads match). */
export interface ZapierConstituentRecord {
  /** dedupe key: the constituent id for "new", `<id>:<updatedAt ms>` for "updated" */
  id: string;
  constituentId: string;
  type: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  orgName: string | null;
  email: string | null;
  phone: string | null;
  tags: string[];
  /** cents */
  lifetimeGiving: number;
  createdAt: string;
  updatedAt: string;
}

export interface ZapierGiftRecord {
  /** dedupe key = gift id */
  id: string;
  constituentId: string;
  constituentName: string;
  /** cents, in the org's base currency */
  amount: number;
  /** decimal string in `currency`, convenient for spreadsheets/accounting zaps */
  amountDecimal: string;
  currency: string;
  date: string;
  fundId: string;
  fundName: string;
  allocations: { fundId: string; fundName: string; amount: number }[];
  paymentMethod: string;
  channel: string;
  receiptNumber: string | null;
  createdAt: string;
}

export interface ZapierTagAddedRecord {
  /** dedupe key = the tag event id */
  id: string;
  constituentId: string;
  displayName: string;
  email: string | null;
  tag: string;
  addedAt: string;
}

export type ZapierTriggerRecord = ZapierConstituentRecord | ZapierGiftRecord | ZapierTagAddedRecord;

/** GET /zapier/me — Zapier's auth test + connection label */
export interface ZapierMe {
  user: { id: string; name: string; email: string };
  organization: { id: string; name: string; currency: string };
  /** how the caller authenticated */
  auth: "oauth" | "api_token" | "session";
  /** effective permissions (granted scopes ∩ role) */
  scopes: string[];
}

// ---------------------------------------------------------------------------
// Zapier actions

/**
 * POST /zapier/actions/find-or-create-constituent. Matches on email (case
 * insensitive). With an Idempotency-Key header and no email, a retry returns
 * the constituent the first call created.
 */
export const zZapierFindOrCreateConstituent = z
  .object({
    email: zEmail.optional(),
    type: z.enum(["individual", "household", "organization", "foundation"]).default("individual"),
    firstName: z.string().trim().max(100).optional(),
    lastName: z.string().trim().max(100).optional(),
    orgName: z.string().trim().max(200).optional(),
    phone: z.string().trim().min(3).max(30).optional(),
    tags: z.array(z.string().trim().min(1).max(60)).max(20).default([]),
    /** false = search only (Zapier "Find Constituent") */
    createIfMissing: z.boolean().default(true),
  })
  .superRefine((v, ctx) => {
    if (!v.email && !v.firstName && !v.lastName && !v.orgName) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["email"], message: "Give an email or a name" });
    }
  });
export type ZapierFindOrCreateConstituent = z.infer<typeof zZapierFindOrCreateConstituent>;

export interface ZapierFindOrCreateResult {
  found: boolean;
  created: boolean;
  constituent: ZapierConstituentRecord | null;
}

/** POST /zapier/actions/create-gift — Idempotency-Key header makes retries safe. */
export const zZapierCreateGift = z
  .object({
    constituentId: zId,
    /** cents (the Zapier app converts the user's decimal amount) */
    amount: zMoney.refine((v) => v > 0, "Amount must be positive"),
    date: zIsoDate.optional(),
    fundId: zId.optional(),
    /** resolved case-insensitively against existing (unarchived) funds */
    fundName: z.string().trim().min(1).max(200).optional(),
    campaignId: zId.optional(),
    paymentMethod: zPaymentMethod.default("other"),
    channel: zGiftChannel.default("other"),
    checkNumber: z.string().trim().max(50).optional(),
    notes: z.string().max(5000).optional(),
    externalId: z.string().max(100).optional(),
  })
  .superRefine((v, ctx) => {
    if (!v.fundId && !v.fundName) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fundId"], message: "Give a fund id or fund name" });
  });
export type ZapierCreateGift = z.infer<typeof zZapierCreateGift>;

/** Idempotency-Key header: printable, ≤ 200 chars */
export const zIdempotencyKey = z.string().trim().min(1).max(200).regex(/^[\x21-\x7e]+$/, "Idempotency-Key must be printable ASCII without spaces");

// ---------------------------------------------------------------------------
// QuickBooks Online

export const QBO_POSTING_MODES = ["journal_entry", "sales_receipt"] as const;
export type QboPostingMode = (typeof QBO_POSTING_MODES)[number];
export const QBO_SYNC_STATUSES = ["pending", "synced", "failed"] as const;
export type QboSyncStatus = (typeof QBO_SYNC_STATUSES)[number];
export type QboSyncKind = "gift" | "reversal";

export const zQboSettings = z
  .object({
    postingMode: z.enum(QBO_POSTING_MODES).optional(),
    /** QBO account (Id or account number) money is deposited to — the debit side */
    depositAccount: z.string().trim().min(1).max(100).nullable().optional(),
    /** only gifts dated on/after this are synced */
    syncFrom: zIsoDate.optional(),
    anonymousCustomerName: z.string().trim().min(1).max(100).optional(),
  })
  .strict();
export type QboSettings = z.infer<typeof zQboSettings>;

export interface QboStatus {
  /** QBO_CLIENT_ID / QBO_CLIENT_SECRET are set on this deployment */
  configured: boolean;
  connected: boolean;
  connection: null | {
    realmId: string;
    /** from QuickBooks CompanyInfo; null until first read */
    companyName: string | null;
    /** the QuickBooks company's home currency (ISO 4217); null until first read */
    companyCurrency: string | null;
    status: "active" | "error" | "disconnected";
    lastError: string | null;
    postingMode: QboPostingMode;
    depositAccount: string | null;
    syncFrom: string;
    anonymousCustomerName: string;
    lastSyncAt: string | null;
    connectedAt: string;
  };
  counts: Record<QboSyncStatus, number>;
  /** set when the QuickBooks home currency differs from the org's base currency — syncing is paused */
  currencyMismatch: QboCurrencyMismatch | null;
}

export interface QboCurrencyMismatch {
  qboCurrency: string;
  orgCurrency: string;
  message: string;
}

export const qboCurrencyMismatchMessage = (qboCurrency: string, orgCurrency: string) =>
  `QuickBooks company currency is ${qboCurrency} but this organization's base currency is ${orgCurrency}. Syncing is paused.`;

export interface QboSyncRow {
  id: string;
  giftId: string;
  kind: QboSyncKind;
  status: QboSyncStatus;
  attempts: number;
  nextAttemptAt: string;
  lastError: string | null;
  qboEntityType: string | null;
  qboEntityId: string | null;
  syncedAt: string | null;
}

export const zQboSyncListQuery = z.object({
  status: z.enum(QBO_SYNC_STATUSES).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const zQboPreviewQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export interface QboPreviewEntry {
  giftId: string;
  kind: QboSyncKind;
  entityType: "SalesReceipt" | "JournalEntry";
  /** the request body that would be posted; account/item refs are unresolved in a preview */
  payload: Record<string, unknown>;
  /** blocking problems (e.g. a fund without a GL account) — the gift would fail */
  problems: string[];
}

export interface QboPreview {
  configured: boolean;
  connected: boolean;
  postingMode: QboPostingMode;
  /** when set, nothing would be posted (every entry carries the message as a problem) */
  currencyMismatch: QboCurrencyMismatch | null;
  entries: QboPreviewEntry[];
}

export interface QboSyncRunResult {
  enqueued: number;
  attempted: number;
  synced: number;
  failed: number;
  /** will be retried later (backoff) */
  retrying: number;
}

// ---------------------------------------------------------------------------
// CT-RM-35: two-way data sync (generic sync connections)
//
// A sync connection pairs this org with a remote system through a connector.
// The only connector shipped is the built-in STUB "Test CRM": a database-backed
// fake remote that lives inside the org's own data. It never makes network
// calls, needs no OAuth or secrets, and is test mode only. Runs are manual
// ("Sync now"); there is no scheduler that calls out.

export const SYNC_CONNECTORS = ["test_crm"] as const;
export type SyncConnectorKey = (typeof SYNC_CONNECTORS)[number];

export const SYNC_CONFLICT_POLICIES = ["newest_wins", "causetide_wins", "remote_wins"] as const;
export type SyncConflictPolicy = (typeof SYNC_CONFLICT_POLICIES)[number];

/** per field: in = remote → CauseTide, out = CauseTide → remote, both = two-way */
export const SYNC_DIRECTIONS = ["in", "out", "both"] as const;
export type SyncDirection = (typeof SYNC_DIRECTIONS)[number];

export const SYNC_OBJECTS = ["constituent", "gift"] as const;
export type SyncObject = (typeof SYNC_OBJECTS)[number];

/** CauseTide constituent fields a connection can map (same names as the CSV import fields) */
export const SYNC_CONSTITUENT_FIELDS = [
  "firstName",
  "lastName",
  "middleName",
  "prefix",
  "suffix",
  "nickname",
  "orgName",
  "birthDate",
  "email",
  "phone",
  "addressLine1",
  "addressLine2",
  "city",
  "state",
  "postalCode",
  "country",
] as const;
export type SyncConstituentField = (typeof SYNC_CONSTITUENT_FIELDS)[number];

/** gift fields: outbound only (gifts are read-only on the remote side) */
export const SYNC_GIFT_FIELDS = ["amount", "date", "fund", "paymentMethod"] as const;
export type SyncGiftField = (typeof SYNC_GIFT_FIELDS)[number];

const zRemoteField = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9_]{0,62}$/, "lowercase letters, digits and underscores");

export const zSyncConstituentFieldMapping = z.object({
  local: z.enum(SYNC_CONSTITUENT_FIELDS),
  remote: zRemoteField,
  direction: z.enum(SYNC_DIRECTIONS),
});
export const zSyncGiftFieldMapping = z.object({
  local: z.enum(SYNC_GIFT_FIELDS),
  remote: zRemoteField,
  direction: z.literal("out"),
});

const noDuplicateFields = (fields: { local: string; remote: string }[], ctx: z.RefinementCtx) => {
  const locals = new Set<string>();
  const remotes = new Set<string>();
  fields.forEach((f, i) => {
    if (locals.has(f.local)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i, "local"], message: `${f.local} is mapped twice` });
    if (remotes.has(f.remote)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i, "remote"], message: `${f.remote} is mapped twice` });
    locals.add(f.local);
    remotes.add(f.remote);
  });
};

export const zSyncMappings = z.object({
  constituent: z.object({
    enabled: z.boolean(),
    fields: z.array(zSyncConstituentFieldMapping).max(SYNC_CONSTITUENT_FIELDS.length).superRefine(noDuplicateFields),
  }),
  gift: z.object({
    enabled: z.boolean(),
    fields: z.array(zSyncGiftFieldMapping).max(SYNC_GIFT_FIELDS.length).superRefine(noDuplicateFields),
  }),
});
export type SyncMappings = z.infer<typeof zSyncMappings>;
export type SyncConstituentFieldMapping = z.infer<typeof zSyncConstituentFieldMapping>;
export type SyncGiftFieldMapping = z.infer<typeof zSyncGiftFieldMapping>;

export interface SyncRemoteField {
  key: string;
  label: string;
}

export interface SyncConnectorInfo {
  key: SyncConnectorKey;
  label: string;
  description: string;
  /** stub connectors run against a fake remote and never call out */
  testMode: true;
  remoteFields: Record<SyncObject, SyncRemoteField[]>;
  defaultMappings: SyncMappings;
}

/** the Test CRM's default mapping: names + contact info two-way, gifts outbound */
export const TEST_CRM_DEFAULT_MAPPINGS: SyncMappings = {
  constituent: {
    enabled: true,
    fields: [
      { local: "firstName", remote: "first_name", direction: "both" },
      { local: "lastName", remote: "last_name", direction: "both" },
      { local: "orgName", remote: "company", direction: "both" },
      { local: "email", remote: "email", direction: "both" },
      { local: "phone", remote: "phone", direction: "both" },
      { local: "city", remote: "city", direction: "out" },
    ],
  },
  gift: {
    enabled: true,
    fields: [
      { local: "amount", remote: "amount_cents", direction: "out" },
      { local: "date", remote: "close_date", direction: "out" },
      { local: "fund", remote: "fund_name", direction: "out" },
    ],
  },
};

export const zSyncConnectionCreate = z
  .object({
    connector: z.enum(SYNC_CONNECTORS),
    name: z.string().trim().min(1).max(100),
    conflictPolicy: z.enum(SYNC_CONFLICT_POLICIES).default("newest_wins"),
    /** omitted: the connector's default mapping */
    mappings: zSyncMappings.optional(),
  })
  .strict();
export type SyncConnectionCreate = z.infer<typeof zSyncConnectionCreate>;

export const zSyncConnectionUpdate = z
  .object({
    name: z.string().trim().min(1).max(100).optional(),
    conflictPolicy: z.enum(SYNC_CONFLICT_POLICIES).optional(),
    mappings: zSyncMappings.optional(),
  })
  .strict();
export type SyncConnectionUpdate = z.infer<typeof zSyncConnectionUpdate>;

export const SYNC_CONNECTION_STATUSES = ["active", "paused"] as const;
export type SyncConnectionStatus = (typeof SYNC_CONNECTION_STATUSES)[number];

export interface SyncConnection {
  id: string;
  connector: SyncConnectorKey;
  connectorLabel: string;
  name: string;
  status: SyncConnectionStatus;
  testMode: true;
  conflictPolicy: SyncConflictPolicy;
  mappings: SyncMappings;
  /** local changes queued from the outbox, waiting for the next run */
  pendingOutbound: number;
  /** records whose last attempt failed (retry from the run log) */
  failedRecords: number;
  lastRunAt: string | null;
  createdAt: string;
}

export const SYNC_RUN_STATUSES = ["running", "completed", "failed"] as const;
export type SyncRunStatus = (typeof SYNC_RUN_STATUSES)[number];

export interface SyncRunCounts {
  /** records written to the remote */
  pushed: number;
  /** remote records applied in CauseTide (created + updated) */
  pulled: number;
  created: number;
  updated: number;
  /** both sides changed the same record; resolved by the conflict policy */
  conflicts: number;
  /** unchanged, superseded, or echoes of our own writes (loop prevention) */
  skipped: number;
  failed: number;
}

export interface SyncRun {
  id: string;
  connectionId: string;
  trigger: "manual" | "retry";
  status: SyncRunStatus;
  counts: SyncRunCounts;
  /** the import batch inbound changes were applied through (rollback from Imports) */
  importBatchId: string | null;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export const SYNC_RECORD_STATUSES = ["pending", "synced", "skipped", "failed"] as const;
export type SyncRecordStatus = (typeof SYNC_RECORD_STATUSES)[number];
export const SYNC_RECORD_DIRECTIONS = ["in", "out"] as const;

export interface SyncRecord {
  id: string;
  runId: string | null;
  direction: "in" | "out";
  object: SyncObject;
  localId: string | null;
  remoteId: string | null;
  status: SyncRecordStatus;
  /** created / updated / unchanged / echo / superseded / conflict:causetide / conflict:remote */
  outcome: string | null;
  changedFields: string[];
  error: string | null;
  attempts: number;
  createdAt: string;
  updatedAt: string;
}

export const zSyncRecordListQuery = z.object({
  runId: zId.optional(),
  status: z.enum(SYNC_RECORD_STATUSES).optional(),
  direction: z.enum(SYNC_RECORD_DIRECTIONS).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type SyncRecordListQuery = z.infer<typeof zSyncRecordListQuery>;

export const zSyncRunListQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

/** a record in the Test CRM fake remote */
export interface SyncTestRemoteRecord {
  remoteId: string;
  object: SyncObject;
  data: Record<string, string | number | null>;
  /** who wrote it last: "causetide" (a sync push) or "remote" (a simulated remote edit) */
  origin: "causetide" | "remote";
  updatedAt: string;
}

/** simulate an edit made in the remote system (Test CRM only) */
export const zSyncTestRemoteEdit = z
  .object({
    /** omitted: a new remote contact */
    remoteId: z.string().trim().min(1).max(100).optional(),
    data: z.record(zRemoteField, z.union([z.string().max(500), z.number(), z.null()])),
    /** backdate the remote change (conflict testing); defaults to now */
    updatedAt: z.string().datetime().optional(),
  })
  .strict();
export type SyncTestRemoteEdit = z.infer<typeof zSyncTestRemoteEdit>;
